// Struttura di una carta: carta > categoria > sottocategoria. Funzioni pure, senza React.
//
// Stato:
//   { categories: [{ key, uuid, name, is_active, products: [item], children: [sub] }], unassigned: [item] }
//   sub  = { key, uuid, name, is_active, products: [item] }
//   item = { uuid, name, vintage, price, is_available }
//
// Contenitori dei vini: "u" (da collocare), "c:<key>" (categoria), "s:<key>" (sottocategoria).

let counter = 0;
const newKey = () => `new-${++counter}`;

export const UNASSIGNED = "u";
export const categoryContainer = (key) => `c:${key}`;
export const subContainer = (key) => `s:${key}`;

const toNode = (n) => ({
  key: n.uuid || newKey(),
  uuid: n.uuid || null,
  name: n.name || "",
  is_active: n.is_active !== false,
  products: n.products || [],
});

// Dalla risposta del backend allo stato dell'editor.
export const fromApi = (data) => ({
  categories: (data?.categories || []).map((c) => ({
    ...toNode(c),
    children: (c.children || []).map(toNode),
  })),
  unassigned: data?.unassigned || [],
});

// Prodotto nascosto: resta nella struttura ma non compare nella carta pubblica ne' in stampa.
const productToApi = (p) => (p.is_hidden ? { uuid: p.uuid, is_hidden: true } : p.uuid);

// Nasconde o mostra un prodotto (ovunque si trovi nella struttura).
export const toggleProduct = (state, uuid) => {
  const flip = (list) => list.map((p) => (String(p.uuid) === String(uuid) ? { ...p, is_hidden: !p.is_hidden } : p));
  return {
    ...state,
    unassigned: flip(state.unassigned),
    categories: state.categories.map((c) => ({ ...c, products: flip(c.products), children: c.children.map((s) => ({ ...s, products: flip(s.products) })) })),
  };
};

// Dallo stato dell'editor al corpo della richiesta di salvataggio.
export const toApi = (state) => ({
  categories: state.categories.map((c) => ({
    ...(c.uuid ? { uuid: c.uuid } : {}),
    name: c.name.trim(),
    is_active: c.is_active,
    products: c.products.map(productToApi),
    children: c.children.map((s) => ({
      ...(s.uuid ? { uuid: s.uuid } : {}),
      name: s.name.trim(),
      is_active: s.is_active,
      products: s.products.map(productToApi),
    })),
  })),
});

// Primo nome vuoto trovato (il backend rifiuta i nomi vuoti), oppure null.
export const emptyNameKey = (state) => {
  for (const c of state.categories) {
    if (!c.name.trim()) return c.key;
    for (const s of c.children) if (!s.name.trim()) return s.key;
  }
  return null;
};

export const countProducts = (state) =>
  state.unassigned.length +
  state.categories.reduce(
    (n, c) => n + c.products.length + c.children.reduce((m, s) => m + s.products.length, 0),
    0
  );

// Elenco di vini di un contenitore.
export const itemsOf = (state, container) => {
  if (container === UNASSIGNED) return state.unassigned;
  const [kind, key] = [container[0], container.slice(2)];
  for (const c of state.categories) {
    if (kind === "c" && c.key === key) return c.products;
    for (const s of c.children) if (kind === "s" && s.key === key) return s.products;
  }
  return [];
};

const withItems = (state, container, items) => {
  if (container === UNASSIGNED) return { ...state, unassigned: items };
  const kind = container[0];
  const key = container.slice(2);
  return {
    ...state,
    categories: state.categories.map((c) => {
      if (kind === "c" && c.key === key) return { ...c, products: items };
      if (kind === "s" && c.children.some((s) => s.key === key)) {
        return { ...c, children: c.children.map((s) => (s.key === key ? { ...s, products: items } : s)) };
      }
      return c;
    }),
  };
};

// Dove si trova un vino: { container, index } oppure null.
export const locateItem = (state, uuid) => {
  const search = (container) => {
    const index = itemsOf(state, container).findIndex((p) => p.uuid === uuid);
    return index >= 0 ? { container, index } : null;
  };
  let found = search(UNASSIGNED);
  if (found) return found;
  for (const c of state.categories) {
    found = search(categoryContainer(c.key)) || c.children.map((s) => search(subContainer(s.key))).find(Boolean);
    if (found) return found;
  }
  return null;
};

// Sposta un vino in un contenitore, alla posizione `index` (come appare, prima di toglierlo).
export const moveItem = (state, uuid, container, index) => {
  const from = locateItem(state, uuid);
  if (!from) return state;
  const item = itemsOf(state, from.container)[from.index];
  let target = index == null ? itemsOf(state, container).length : index;
  if (from.container === container && from.index < target) target -= 1;
  const without = withItems(state, from.container, itemsOf(state, from.container).filter((p) => p.uuid !== uuid));
  const list = itemsOf(without, container).slice();
  list.splice(Math.max(0, Math.min(target, list.length)), 0, item);
  return withItems(without, container, list);
};

// Sposta una categoria davanti a `beforeKey` (null = in fondo).
export const moveCategory = (state, key, beforeKey) => {
  const node = state.categories.find((c) => c.key === key);
  if (!node || key === beforeKey) return state;
  const rest = state.categories.filter((c) => c.key !== key);
  const at = beforeKey == null ? rest.length : rest.findIndex((c) => c.key === beforeKey);
  rest.splice(at < 0 ? rest.length : at, 0, node);
  return { ...state, categories: rest };
};

// Sposta una sottocategoria nella categoria `toCategoryKey`, davanti a `beforeKey` (null = in fondo).
export const moveSub = (state, key, toCategoryKey, beforeKey) => {
  let sub = null;
  for (const c of state.categories) {
    const found = c.children.find((s) => s.key === key);
    if (found) sub = found;
  }
  if (!sub || key === beforeKey || !state.categories.some((c) => c.key === toCategoryKey)) return state;
  return {
    ...state,
    categories: state.categories.map((c) => {
      let children = c.children.filter((s) => s.key !== key);
      if (c.key === toCategoryKey) {
        const at = beforeKey == null ? children.length : children.findIndex((s) => s.key === beforeKey);
        children = children.slice();
        children.splice(at < 0 ? children.length : at, 0, sub);
      }
      return { ...c, children };
    }),
  };
};

// Un posto su o giu' (frecce): categorie tra loro, sottocategorie dentro la loro categoria.
export const shift = (state, key, delta) => {
  const swap = (list) => {
    const i = list.findIndex((n) => n.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return list;
    const copy = list.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };
  if (state.categories.some((c) => c.key === key)) return { ...state, categories: swap(state.categories) };
  return { ...state, categories: state.categories.map((c) => ({ ...c, children: swap(c.children) })) };
};

export const addCategory = (state, name = "") => {
  const node = { ...toNode({ name }), children: [] };
  return { state: { ...state, categories: [...state.categories, node] }, key: node.key };
};

export const addSub = (state, categoryKey, name = "") => {
  const node = toNode({ name });
  return {
    state: {
      ...state,
      categories: state.categories.map((c) => (c.key === categoryKey ? { ...c, children: [...c.children, node] } : c)),
    },
    key: node.key,
  };
};

const mapNode = (state, key, fn) => ({
  ...state,
  categories: state.categories.map((c) =>
    c.key === key ? fn(c) : { ...c, children: c.children.map((s) => (s.key === key ? fn(s) : s)) }
  ),
});

export const renameNode = (state, key, name) => mapNode(state, key, (n) => ({ ...n, name }));

// Nascondere una categoria nasconde anche le sue sottocategorie nella carta pubblica.
export const toggleNode = (state, key) => mapNode(state, key, (n) => ({ ...n, is_active: !n.is_active }));

// Elimina una categoria o sottocategoria: i suoi vini tornano tra quelli da collocare.
export const removeNode = (state, key) => {
  const freed = [];
  const categories = [];
  for (const c of state.categories) {
    if (c.key === key) {
      freed.push(...c.products, ...c.children.flatMap((s) => s.products));
      continue;
    }
    const children = [];
    for (const s of c.children) {
      if (s.key === key) freed.push(...s.products);
      else children.push(s);
    }
    categories.push({ ...c, children });
  }
  return { ...state, categories, unassigned: [...state.unassigned, ...freed] };
};

// Sposta l'elemento in posizione `from` davanti a quello in posizione `to` (indice prima dello spostamento).
export const moveInList = (list, from, to) => {
  if (from < 0 || from >= list.length || from === to || from + 1 === to) return list;
  const copy = list.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to > from ? to - 1 : to, 0, moved);
  return copy;
};
